import { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getTeachersDirect } from '@timetable/api';
import { createAbsence } from '@timetable/api';
import type { CreateAbsenceRequest } from '@timetable/types';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '../Toast';
import { queryKeys } from '../../lib/queryKeys';

interface AbsenceFormProps {
  /**
   * Called after a successful submission with the newly created absence ID.
   * The parent can use this to scroll/highlight the new row.
   */
  onSuccess?: (absenceId: string) => void;
}

export function AbsenceForm({ onSuccess }: AbsenceFormProps) {
  const queryClient = useQueryClient();
  const { schoolContext } = useAuthStore();
  const { success: showSuccess } = useToast();

  // Form state
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [absenceType, setAbsenceType] = useState<'full_day' | 'temporary_duty'>('full_day');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [reason, setReason] = useState('');

  // UI state
  const [error, setError] = useState<string | null>(null);

  // Fetch teachers
  const { data: teachers, isLoading: loadingTeachers } = useQuery({
    queryKey: queryKeys.teachers.list({ school_id: schoolContext || '' }),
    queryFn: () => getTeachersDirect({ school_id: schoolContext || undefined }),
    enabled: !!schoolContext,
  });

  // Handle start date change — auto-advance end date if it falls behind (Req 1.1)
  const handleStartDateChange = (newStart: Date | null) => {
    setStartDate(newStart);
    if (newStart) {
      // If end date is before new start date, reset end date to start date
      if (!endDate || endDate < newStart) {
        setEndDate(newStart);
      }
      // Temporary Duty: end date is always locked to start date (Req 1.5)
      if (absenceType === 'temporary_duty') {
        setEndDate(newStart);
      }
    }
  };

  // Handle absence type change — lock end date for Temporary Duty (Req 1.5)
  const handleAbsenceTypeChange = (newType: 'full_day' | 'temporary_duty') => {
    setAbsenceType(newType);
    if (newType === 'temporary_duty' && startDate) {
      setEndDate(startDate);
    }
  };

  // Record absence mutation using the new createAbsence API
  const recordAbsenceMutation = useMutation({
    mutationFn: (absence: CreateAbsenceRequest) => createAbsence(absence),
    onSuccess: (data) => {
      // Invalidate queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['absences'] });
      queryClient.invalidateQueries({ queryKey: ['assignments'] });

      // Show success snackbar (Req 1.13)
      showSuccess('Absence recorded successfully');

      // Reset form to defaults
      setSelectedTeacherId('');
      setStartDate(new Date());
      setEndDate(new Date());
      setAbsenceType('full_day');
      setStartTime('');
      setEndTime('');
      setReason('');
      setError(null);

      // Notify parent so it can scroll/highlight the new row (Req 1.13)
      onSuccess?.(data.id);
    },
    onError: (err: any) => {
      // Surface overlap validation errors inline (Req 1.9)
      // ConflictError has a `conflicts` property; some callers may use `data`
      const conflictList = err?.conflicts || err?.data;
      if (Array.isArray(conflictList) && conflictList.length > 0) {
        const conflicts = conflictList as Array<{ start_date: string; end_date: string }>;
        const conflictDates = conflicts
          .map((c) => `${c.start_date} – ${c.end_date}`)
          .join(', ');
        setError(`This teacher already has an absence during this period: ${conflictDates}`);
      } else {
        setError(err.message || 'Failed to record absence');
      }
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Client-side validation
    if (!startDate) {
      setError('Please select a start date');
      return;
    }

    if (!endDate) {
      setError('Please select an end date');
      return;
    }

    // Req 1.4: end date must not be before start date
    if (endDate < startDate) {
      setError('End date must be on or after start date');
      return;
    }

    if (!selectedTeacherId) {
      setError('Please select a teacher');
      return;
    }

    // Req 1.6: Temporary Duty requires start/end times
    if (absenceType === 'temporary_duty' && (!startTime || !endTime)) {
      setError('Please specify start and end times for Temporary Duty');
      return;
    }

    // Req 1.6: start time must be before end time
    if (absenceType === 'temporary_duty' && startTime >= endTime) {
      setError('Start time must be before end time');
      return;
    }

    const schoolId = schoolContext;
    if (!schoolId) {
      setError('School context not found. Please log in again.');
      return;
    }

    const startDateStr = startDate.toISOString().split('T')[0];
    const endDateStr = endDate.toISOString().split('T')[0];

    const absenceData: CreateAbsenceRequest = {
      school_id: schoolId,
      teacher_id: selectedTeacherId,
      start_date: startDateStr,
      end_date: endDateStr,
      absence_type: absenceType,
      reason: reason || undefined,
    };

    if (absenceType === 'temporary_duty') {
      absenceData.start_time = startTime;
      absenceData.end_time = endTime;
    }

    recordAbsenceMutation.mutate(absenceData);
  };

  const isSubmitting = recordAbsenceMutation.isPending;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        {/* Inline error (includes overlap validation — Req 1.9) */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Teacher Selector (Req 1.1) */}
        <FormControl fullWidth required>
          <TextField
            select
            label="Teacher"
            value={selectedTeacherId}
            onChange={(e) => setSelectedTeacherId(e.target.value)}
            disabled={loadingTeachers || isSubmitting}
            required
          >
            {loadingTeachers ? (
              <MenuItem value="" disabled>
                Loading teachers…
              </MenuItem>
            ) : (
              teachers?.map((teacher) => (
                <MenuItem key={teacher.id} value={teacher.id}>
                  {teacher.name}
                </MenuItem>
              ))
            )}
          </TextField>
        </FormControl>

        {/* Absence Type (Req 1.1) */}
        <FormControl fullWidth required>
          <TextField
            select
            label="Absence Type"
            value={absenceType}
            onChange={(e) => handleAbsenceTypeChange(e.target.value as 'full_day' | 'temporary_duty')}
            disabled={isSubmitting}
            required
          >
            <MenuItem value="full_day">Full Day</MenuItem>
            <MenuItem value="temporary_duty">Temporary Duty (Partial Day)</MenuItem>
          </TextField>
        </FormControl>

        {/* Start Date Picker (Req 1.1) */}
        <DatePicker
          label="Start Date"
          value={startDate}
          onChange={handleStartDateChange}
          disabled={isSubmitting}
          slotProps={{
            textField: {
              required: true,
              fullWidth: true,
            },
          }}
        />

        {/* End Date Picker — disabled and locked for Temporary Duty (Req 1.5) */}
        <DatePicker
          label="End Date"
          value={endDate}
          onChange={(newEnd) => {
            if (absenceType !== 'temporary_duty') {
              setEndDate(newEnd);
            }
          }}
          disabled={isSubmitting || absenceType === 'temporary_duty'}
          minDate={startDate || undefined}
          slotProps={{
            textField: {
              required: true,
              fullWidth: true,
              helperText:
                absenceType === 'temporary_duty'
                  ? 'Locked to start date for Temporary Duty'
                  : 'Defaults to start date for single-day absence',
            },
          }}
        />

        {/* Time Range — required only for Temporary Duty (Req 1.1, 1.6) */}
        {absenceType === 'temporary_duty' && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              type="time"
              label="Start Time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              disabled={isSubmitting}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="time"
              label="End Time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              disabled={isSubmitting}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        )}

        {/* Reason (optional) (Req 1.1) */}
        <TextField
          label="Reason (Optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={isSubmitting}
          multiline
          rows={3}
          fullWidth
        />

        {/* Submit Button */}
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting || loadingTeachers}
          fullWidth
          size="large"
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Recording Absence…
            </>
          ) : (
            'Record Absence'
          )}
        </Button>
      </Box>
    </LocalizationProvider>
  );
}
